import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';

import dayjs from 'dayjs';
import { z } from 'zod';

import { usePostCreateTransferInfoApi, usePostPaymentApprovalCallbackApi, usePostPaymentReadyApi } from '@/api/payment';
import CardIcon from '@/components/Icon/CardIcon';
import CardIconActive from '@/components/Icon/CardIconActive';
import MoneyIcon from '@/components/Icon/MoneyIcon';
import MoneyIconActive from '@/components/Icon/MoneyIconActive';
import { CONF_TITLE } from '@/constants/menu';
import { convertPaymentRegisterValue, convertPaymentValue, convertPublicationPaymentValue } from '@/utils/convertValue';

import Field from '../../Field/Field';

interface RegisterField {
	payment: string;
	member: string;
	publicationType: string;
	// cardNumber?: { first: string; second: string; third: string; fourth: string };
	// periodDate?: { month: string; year: string };
	// owner?: string;
	// payMonth?: string;
}

type GoodsName = '연회비' | '등록비' | '게재료';

interface PaymentModalProps {
	closeModal: (bool: boolean) => void;
	goodsName: GoodsName;
	memberType?: string;
}
const PaymentModal = ({ closeModal, goodsName, memberType }: PaymentModalProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const year = dayjs().format('YYYY');
	const userRegisterSchema = z.object({
		payment: z.string(),
		member: z.string(),
		publicationType: z.string(),
	});

	const { register, handleSubmit, watch, setValue } = useForm<RegisterField>({
		resolver: zodResolver(userRegisterSchema),
		mode: 'onChange',
		defaultValues: {
			payment: 'card',
			member: memberType,
			publicationType: '기본',
		},
	});

	const { mutateAsync: paymentReady } = usePostPaymentReadyApi();
	const { mutateAsync: paymentApprovalCallback } = usePostPaymentApprovalCallbackApi();
	const { mutateAsync: createTransferInfo } = usePostCreateTransferInfoApi();

	useEffect(() => {
		if (memberType) {
			setValue('member', memberType);
		}
	}, [memberType, setValue]);

	const getAmount = () => {
		if (goodsName === '연회비') {
			return convertPaymentValue(watch('member'));
		}
		if (goodsName === '등록비') {
			return convertPaymentRegisterValue(watch('member'));
		}
		return convertPublicationPaymentValue(watch('publicationType'));
	};

	const getGoodsName = () => {
		if (goodsName === '연회비') {
			return `${year}년도 연회비`;
		}
		if (goodsName === '등록비') {
			return CONF_TITLE;
		}
		if (goodsName === '게재료') {
			return watch('publicationType') === '속보' ? '속보(급행) 게재료' : '기본 게재료';
		}
		return '';
	};

	const getMemberType = () => memberType ?? watch('member') ?? '';

	const handleModalClick = (payment: string) => {
		setValue('payment', payment);
	};

	const onSubmit = async (_: RegisterField) => {
		if (isOpen) {
			closeModal(false);
		}
		if (watch('payment') === 'account' && !isOpen) {
			const res = await createTransferInfo({
				member_type: getMemberType(),
				amount: getAmount().replace(/,/g, ''),
				goods_type: goodsName === '게재료' ? '게재료' : goodsName,
				goods_name: getGoodsName(),
			});
			if (res.data?.success) {
				console.log('createTransferInfo', res);
				setIsOpen(true);
				return;
			}
			return;
		}
		if (watch('payment') === 'card') {
			await paymentReady({
				member_type: getMemberType(),
				amount: Number(getAmount().replace(/,/g, '')),
				goods_type: goodsName === '게재료' ? '게재료' : goodsName,
				goods_name: getGoodsName(),
			});
		}
	};

	return (
		<PaymentContainer onSubmit={handleSubmit(onSubmit)}>
			<Title>{isOpen ? '무통장 입금' : goodsName === '연회비' ? `${year} 연회비 납부` : goodsName === '등록비' ? '학회등록비 납부' : '게재료 납부'}</Title>

			{!isOpen && (
				<>
					{goodsName === '연회비' ? (
						<Field title="회원구분" gap="0.8rem">
							<GenderBox>
								<GenderRadio>
									<input type="radio" value="일반회원" {...register('member')} disabled={!!memberType} />
									일반회원
								</GenderRadio>
								<GenderRadio>
									<input type="radio" value="학생회원" {...register('member')} disabled={!!memberType} />
									학생회원
								</GenderRadio>
							</GenderBox>
						</Field>
					) : (
						<AmountBox>
							<div>결제 금액</div>
							<div className="point">
								<strong>{getAmount()}</strong> 원
							</div>
						</AmountBox>
					)}
					{goodsName === '게재료' && (
						<Field title="게재 구분" gap="0.8rem">
							<GenderBox>
								<GenderRadio>
									<input type="radio" value="기본" {...register('publicationType')} />
									기본
								</GenderRadio>
								<GenderRadio>
									<input type="radio" value="속보" {...register('publicationType')} />
									속보(급행)
								</GenderRadio>
							</GenderBox>
						</Field>
					)}

					<Field title="결제 수단" gap="0.8rem">
						<PaymentContetnBox>
							<PaymentContent className={watch('payment') === 'card' ? 'active' : ''} onClick={() => handleModalClick('card')}>
								{watch('payment') === 'card' ? <CardIconActive /> : <CardIcon />} 카드결제
							</PaymentContent>
							<PaymentContent className={watch('payment') === 'account' ? 'active' : ''} onClick={() => handleModalClick('account')}>
								{watch('payment') === 'account' ? <MoneyIconActive /> : <MoneyIcon />} 무통장 입금
							</PaymentContent>
						</PaymentContetnBox>
					</Field>
				</>
			)}
			{isOpen && (
				<PaymentAccountBox>
					{goodsName === '연회비' && (
						<div className="item">
							<p className="title">회원구분</p>
							<p className="content point">
								<p className="content">{watch('member')}</p>
							</p>
						</div>
					)}
					{goodsName === '게재료' && (
						<div className="item">
							<p className="title">게재 구분</p>
							<p className="content">{watch('publicationType') === '속보' ? '속보(급행) 게재료' : '기본 게재료'}</p>
						</div>
					)}

					<div className="item">
						<p className="title">입금금액</p>
						<p className="content point">
							<strong className="point_bold">{getAmount()}</strong>원
						</p>
					</div>
					<div className="item">
						<p className="title">은행계좌</p>
						<p className="content">
							국민은행&nbsp;
							<strong className="point_bold">989801-00-042216</strong>
						</p>
					</div>
					<div className="item">
						<p className="title">예금주</p>
						<p className="content">사단법인 대한화장품학회</p>
					</div>
				</PaymentAccountBox>
			)}

			<PaymentButtonBox>
				{!isOpen && (
					<CancelButton type="button" onClick={() => closeModal(false)}>
						취소
					</CancelButton>
				)}
				<PaymentConfirmButton type="submit">{!isOpen ? '결제' : '확인'}</PaymentConfirmButton>
			</PaymentButtonBox>
		</PaymentContainer>
	);
};

export default PaymentModal;

const PaymentContainer = styled.form`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 2rem;
`;
const Title = styled.h2`
	font-size: ${(props) => props.theme.fontSize.sizeXl};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.3;
	letter-spacing: -0.03em;
	color: ${(props) => props.theme.colors.textPrimary};
`;
const PaymentTitle = styled.div`
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	letter-spacing: -0.6px;
	line-height: 130%;
	&.top {
		margin-top: 2rem;
	}
`;
const AmountBox = styled(PaymentTitle)`
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	.point {
		color: ${(props) => props.theme.colors.primary400};
		font-size: ${(props) => props.theme.fontSize.sizeLg};
		font-weight: ${(props) => props.theme.fontWeight.weightRegular};
		strong {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		}
	}
`;
const PaymentButtonBox = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	gap: 1.2rem;
`;
const PaymentConfirmButton = styled.button`
	background-color: ${(props) => props.theme.colors.primary400};
	color: ${(props) => props.theme.colors.surfacePage};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightMedium};
	letter-spacing: -0.02em;
	line-height: 1.2;
	width: 100%;
	padding: 0 1.2rem;
	height: ${(props) => props.theme.heights.buttonMd};
	border: none;
	border-radius: ${(props) => props.theme.borderRadius.sm};
	cursor: pointer;
	transition:
		background-color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard},
		box-shadow ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard};
	:hover:not(:disabled) {
		background-color: ${(props) => props.theme.colors.primaryHover};
		box-shadow: 0 2px 8px rgba(18, 83, 158, 0.2);
	}
	:active:not(:disabled) {
		background-color: ${(props) => props.theme.colors.primaryActive};
	}
	&:disabled {
		background-color: ${(props) => props.theme.colors.borderSubtle};
		color: ${(props) => props.theme.colors.textMuted};
		cursor: not-allowed;
		box-shadow: none;
	}
`;
const CancelButton = styled(PaymentConfirmButton)`
	background-color: ${(props) => props.theme.colors.gray100};
	color: ${(props) => props.theme.colors.textPrimary};
	box-shadow: none;
	:hover:not(:disabled) {
		background-color: ${(props) => props.theme.colors.borderDefault};
		box-shadow: none;
	}
	:active:not(:disabled) {
		background-color: ${(props) => props.theme.colors.borderDefault};
	}
`;

const PaymentContetnBox = styled.div`
	display: flex;
	gap: 0.8rem;
	width: 100%;
`;
const PaymentContent = styled.div`
	display: flex;
	height: 7.2rem;
	align-items: center;
	justify-content: center;
	width: 100%;
	gap: 0.8rem;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 120%;
	letter-spacing: -0.02em;
	color: ${(props) => props.theme.colors.textMuted};
	background-color: ${(props) => props.theme.colors.surfacePage};
	padding: 0rem 1.2rem 0rem 0.8rem;
	border: 1px solid ${(props) => props.theme.colors.borderDefault};
	cursor: pointer;
	transition:
		border-color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard},
		color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard},
		background-color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard};
	&:hover:not(.active) {
		background-color: ${(props) => props.theme.colors.surfaceMuted};
		border-color: ${(props) => props.theme.colors.borderDefault};
	}
	&.active {
		border: 2px solid ${(props) => props.theme.colors.primary400};
		color: ${(props) => props.theme.colors.primary400};
	}
`;
const GenderBox = styled.div`
	display: flex;
	gap: 4rem;
`;
const GenderRadio = styled.div`
	display: flex;
	gap: 0.4rem;
	align-items: center;
	input[type='radio'] {
		-webkit-appearance: none;
		-moz-appearance: none;
		appearance: none;
		width: 2rem;
		height: 2rem;
		border: 1px solid #ced4da;
		border-radius: ${(props) => props.theme.borderRadius.circle};
		outline: none;
		cursor: pointer;
	}
	input[type='radio']:checked {
		background-color: #fff; // 체크 시 내부 원 색상
		border: 6px solid ${(props) => props.theme.colors.primary400};
	}
`;
const PaymentAccountBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	background-color: #fff;
	.item {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
		.title {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
			line-height: 130%;
			letter-spacing: -0.6px;
			color: #212529;
		}
		.content {
			font-size: ${(props) => props.theme.fontSize.sizeLg};
			font-weight: ${(props) => props.theme.fontWeight.weightRegular};
			line-height: 130%;
			letter-spacing: -0.6px;
			color: #212529;
		}
		.point_bold {
			color: ${(props) => props.theme.colors.primary400};
			font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		}
		.point {
			color: ${(props) => props.theme.colors.primary400};
		}
	}
`;
